import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "./useCurrentUser";
import { toast } from "./use-toast";

export interface UserConnection {
  id: string;
  requester_id: string;
  recipient_id: string;
  status: 'pending' | 'accepted' | 'declined' | 'blocked';
  connection_type: string | null;
  connection_strength: number;
  relationship_context: string | null;
  endorsed_skills: any[];
  connected_since: string | null;
  created_at: string;
  requester?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    professional_title: string | null;
  };
  recipient?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    professional_title: string | null;
  };
}

export function useUserConnections() {
  const { data: user } = useCurrentUser();
  const queryClient = useQueryClient();

  const { data: connections, isLoading } = useQuery({
    queryKey: ["user-connections", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("user_connections")
        .select(`
          *,
          requester:profiles!user_connections_requester_id_fkey(id, full_name, avatar_url, professional_title),
          recipient:profiles!user_connections_recipient_id_fkey(id, full_name, avatar_url, professional_title)
        `)
        .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as UserConnection[];
    },
    enabled: !!user,
  });

  const { data: pendingRequests } = useQuery({
    queryKey: ["pending-connection-requests", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("user_connections")
        .select(`
          *,
          requester:profiles!user_connections_requester_id_fkey(id, full_name, avatar_url, professional_title)
        `)
        .eq('recipient_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as UserConnection[];
    },
    enabled: !!user,
  });

  const createConnectionMutation = useMutation({
    mutationFn: async ({ recipientId, connectionType, relationshipContext }: {
      recipientId: string;
      connectionType?: string;
      relationshipContext?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('connection-request', {
        body: {
          action: 'create',
          recipientId,
          connectionType,
          relationshipContext
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["user-connections"] });
      queryClient.invalidateQueries({ queryKey: ["connection-status"] });
      
      if (data?.alreadyExists) {
        const status = data.connection?.status;
        if (status === 'pending') {
          toast({
            title: "Request Already Sent",
            description: "You already have a pending connection request with this user",
          });
        } else if (status === 'accepted') {
          toast({
            title: "Already Connected",
            description: "You're already connected with this user",
          });
        }
      } else {
        toast({
          title: "Connection Request Sent",
          description: "Your connection request has been sent successfully",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Send Request",
        description: error.message || "Could not send connection request",
        variant: "destructive",
      });
    },
  });

  const respondToConnectionMutation = useMutation({
    mutationFn: async ({ connectionId, action }: {
      connectionId: string;
      action: 'accept' | 'decline';
    }) => {
      const { data, error } = await supabase.functions.invoke('connection-request', {
        body: {
          action,
          connectionId
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user-connections"] });
      queryClient.invalidateQueries({ queryKey: ["pending-connection-requests"] });
      toast({
        title: variables.action === 'accept' ? "Connection Accepted" : "Connection Declined",
        description: variables.action === 'accept' 
          ? "You are now connected" 
          : "Connection request declined",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to respond to connection",
        variant: "destructive",
      });
    },
  });

  const removeConnectionMutation = useMutation({
    mutationFn: async (connectionId: string) => {
      const { data, error } = await supabase.functions.invoke('connection-request', {
        body: {
          action: 'remove',
          connectionId
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-connections"] });
      toast({
        title: "Connection Removed",
        description: "Connection has been removed successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to remove connection",
        variant: "destructive",
      });
    },
  });

  const acceptedConnections = connections?.filter(c => c.status === 'accepted') || [];
  const sentRequests = connections?.filter(c => 
    c.status === 'pending' && c.requester_id === user?.id
  ) || [];

  return {
    connections: acceptedConnections,
    allConnections: connections || [],
    pendingRequests: pendingRequests || [],
    sentRequests,
    isLoading,
    createConnection: createConnectionMutation.mutate,
    acceptConnection: (connectionId: string) => respondToConnectionMutation.mutate({ connectionId, action: 'accept' }),
    declineConnection: (connectionId: string) => respondToConnectionMutation.mutate({ connectionId, action: 'decline' }),
    removeConnection: removeConnectionMutation.mutate,
    isCreating: createConnectionMutation.isPending,
    isResponding: respondToConnectionMutation.isPending,
    isRemoving: removeConnectionMutation.isPending,
  };
}

// Hook to check connection status with a specific user
export function useConnectionStatus(targetUserId: string | null) {
  const { data: user } = useCurrentUser();

  return useQuery({
    queryKey: ["connection-status", user?.id, targetUserId],
    queryFn: async () => {
      if (!user || !targetUserId || user.id === targetUserId) {
        return { isConnected: false, connectionId: null, status: null };
      }

      const { data } = await supabase
        .from("user_connections")
        .select("id, status")
        .or(`and(requester_id.eq.${user.id},recipient_id.eq.${targetUserId}),and(requester_id.eq.${targetUserId},recipient_id.eq.${user.id})`)
        .maybeSingle();

      return {
        isConnected: data?.status === 'accepted',
        connectionId: data?.id || null,
        status: data?.status || null,
      };
    },
    enabled: !!user && !!targetUserId && user.id !== targetUserId,
  });
}
